import { useEffect, useMemo } from 'react';
import { useTokens } from '@vibesites/runtime/react';
import { getContrastTextColor } from '../utils/luminance';

const STYLE_ID = 'belmont-site-cta';

/**
 * Two unified site-wide CTA styles:
 *
 *  .site-cta.site-cta--primary   → outlined, uses --vibe-color-primary,
 *                                  fills with primary on hover
 *  .site-cta.site-cta--secondary → filled with --vibe-color-secondary,
 *                                  swaps to --vibe-color-primary on hover
 *
 * Individual sections can override the accent colours by setting the
 * `--site-cta-primary-accent`, `--site-cta-primary-on-accent`,
 * `--site-cta-secondary-accent`, `--site-cta-secondary-on-accent`,
 * `--site-cta-hover-accent`, or `--site-cta-hover-on-accent` custom
 * properties on any ancestor (useful when the CTA lives on a
 * primary-coloured background, for example).
 *
 * Hover / on-accent text defaults to luminance-based ink published as
 * `--site-cta-ink-on-{primary,secondary,surface}` so hover labels stay
 * readable when a property swaps brand colors.
 */
const SITE_CTA_CSS = `
.site-cta {
  --_site-cta-primary-accent: var(--site-cta-primary-accent, var(--vibe-color-primary));
  --_site-cta-primary-on-accent: var(--site-cta-primary-on-accent, var(--site-cta-ink-on-primary, var(--vibe-color-text-on-primary, #ffffff)));
  --_site-cta-secondary-accent: var(--site-cta-secondary-accent, var(--vibe-color-secondary));
  --_site-cta-secondary-on-accent: var(--site-cta-secondary-on-accent, var(--site-cta-ink-on-secondary, var(--vibe-color-text-on-secondary, #ffffff)));
  --_site-cta-hover-accent: var(--site-cta-hover-accent, var(--vibe-color-primary));
  --_site-cta-hover-on-accent: var(--site-cta-hover-on-accent, var(--site-cta-ink-on-primary, var(--vibe-color-text-on-primary, #ffffff)));

  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  min-width: 11rem;
  border-radius: 999px;
  border: 2px solid transparent;
  font-family: var(--vibe-font-body, inherit);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  line-height: 1;
  text-transform: uppercase;
  text-decoration: none;
  cursor: pointer;
  transition:
    background-color 0.25s ease,
    color 0.25s ease,
    border-color 0.25s ease,
    transform 0.2s ease,
    box-shadow 0.25s ease;
}
.site-cta:hover {
  transform: translateY(-2px);
}
.site-cta:focus-visible {
  outline: 2px solid var(--_site-cta-primary-accent);
  outline-offset: 3px;
}

.site-cta--primary {
  background: transparent;
  border-color: var(--_site-cta-primary-accent);
  color: var(--_site-cta-primary-accent);
}
.site-cta--primary:hover {
  background: var(--_site-cta-primary-accent);
  color: var(--_site-cta-primary-on-accent);
}

.site-cta--secondary {
  background: var(--_site-cta-secondary-accent);
  border-color: var(--_site-cta-secondary-accent);
  color: var(--_site-cta-secondary-on-accent);
}
.site-cta--secondary:hover {
  background: var(--_site-cta-hover-accent);
  border-color: var(--_site-cta-hover-accent);
  color: var(--_site-cta-hover-on-accent);
}

@media (prefers-reduced-motion: reduce) {
  .site-cta,
  .site-cta:hover {
    transform: none;
    transition: background-color 0.25s ease, color 0.25s ease, border-color 0.25s ease;
  }
}
`;

/**
 * Injects the unified `.site-cta` / `.site-cta--primary` /
 * `.site-cta--secondary` CSS into <head> exactly once, and publishes
 * luminance-based `--site-cta-ink-on-*` CSS variables from the active
 * design tokens so hover text contrast tracks each property's palette.
 */
export function useSiteCtaStyles(): void {
  const tokens = useTokens();

  const inks = useMemo(
    () => ({
      primary: getContrastTextColor(tokens?.color?.primary),
      secondary: getContrastTextColor(tokens?.color?.secondary),
      surface: getContrastTextColor(tokens?.color?.surface),
    }),
    [tokens?.color?.primary, tokens?.color?.secondary, tokens?.color?.surface],
  );

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--site-cta-ink-on-primary', inks.primary);
    root.style.setProperty('--site-cta-ink-on-secondary', inks.secondary);
    root.style.setProperty('--site-cta-ink-on-surface', inks.surface);
  }, [inks]);

  useEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = SITE_CTA_CSS;
    document.head.appendChild(style);
  }, []);
}
